/**
 * Pure, React-free helpers for the Workstream domain: id generation, stage
 * badge tones, blank-draft / draft-from-workstream factories, multi-value
 * parsing, validation, filtering, the board grouping/sorting the board view
 * relies on, and date formatting.
 *
 * The AI structuring (AUTO-COLLECT → AI-STRUCTURE) lives in {@link ./aiStructure};
 * this file is presentation/plumbing glue.
 */

import { v4 as uuid } from 'uuid';
import type { BadgeTone } from '../../ui';
import { DEFAULT_STAGE, stageRank } from './workstreamConfig';
import type {
  Workstream,
  WorkstreamDraft,
  WorkstreamFilter,
  WorkstreamSummary,
} from './types';

/** Badge tone for a stage (falls back to neutral for custom stages). */
export function stageTone(stage: string): BadgeTone {
  switch (stage) {
    case 'Design':
      return 'info';
    case 'Build':
      return 'accent';
    case 'Load Testing':
      return 'warning';
    case 'Production Rollout':
      return 'accent';
    case 'Complete':
      return 'success';
    default:
      return 'neutral';
  }
}

/** Deterministic document id for a new workstream. */
export function workstreamDocId(): string {
  return `ws_${uuid()}`;
}

/** Creates an empty draft for the create form (optionally seeding the owner). */
export function blankWorkstreamDraft(engineerAlias = ''): WorkstreamDraft {
  return {
    engineer: engineerAlias,
    title: '',
    why: '',
    whatChanging: '',
    systemsAffected: [],
    technicalApproach: '',
    expectedImpact: [],
    currentStage: DEFAULT_STAGE,
    sprintStoryIds: [],
    goalId: undefined,
    dependencies: [],
    poc: engineerAlias,
  };
}

/** Projects an existing workstream into an editable draft. */
export function draftFromWorkstream(ws: Workstream): WorkstreamDraft {
  return {
    engineer: ws.engineer,
    title: ws.title,
    why: ws.why,
    whatChanging: ws.whatChanging,
    systemsAffected: ws.systemsAffected ?? [],
    technicalApproach: ws.technicalApproach,
    expectedImpact: ws.expectedImpact ?? [],
    currentStage: ws.currentStage,
    sprintStoryIds: ws.sprintStoryIds ?? [],
    goalId: ws.goalId,
    dependencies: ws.dependencies ?? [],
    poc: ws.poc,
  };
}

/**
 * Parses a comma / newline separated string into a normalized, de-duped list
 * (trimmed, empties dropped, original order preserved).
 */
export function parseList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[,\n]/)) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Joins a list back into a comma-separated string for an input field. */
export function listToInput(list?: string[]): string {
  return (list ?? []).join(', ');
}

/** Joins a list into a newline-separated string for a textarea. */
export function listToLines(list?: string[]): string {
  return (list ?? []).join('\n');
}

/**
 * Validates a draft for save. Returns a list of human-readable problems; an
 * empty list means the draft is valid.
 */
export function validateWorkstreamDraft(draft: WorkstreamDraft): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push('A workstream title is required.');
  if (!draft.engineer.trim()) problems.push('An engineer (owner) is required.');
  if (!draft.currentStage.trim()) problems.push('A current stage is required.');
  if (!draft.why.trim()) problems.push('A "why" is required — context is the point.');
  return problems;
}

/** Applies an engineer / stage filter (empty fields match everything). */
export function filterWorkstreams(
  items: WorkstreamSummary[],
  filter: WorkstreamFilter,
): WorkstreamSummary[] {
  const engineer = filter.engineer.trim();
  const stage = filter.stage.trim();
  return items.filter((w) => {
    if (engineer && w.engineer !== engineer) return false;
    if (stage && w.currentStage !== stage) return false;
    return true;
  });
}

/** Unique, sorted engineer aliases present across the given workstreams. */
export function collectEngineers(items: WorkstreamSummary[]): string[] {
  return uniqueSorted(items.map((w) => w.engineer));
}

/** Unique stage values present, ordered along the pipeline. */
export function collectStages(items: WorkstreamSummary[]): string[] {
  const set = new Set<string>();
  for (const w of items) {
    const t = (w.currentStage ?? '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => stageRank(a) - stageRank(b));
}

function uniqueSorted(values: string[]): string[] {
  const set = new Set<string>();
  for (const v of values) {
    const t = (v ?? '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Sorts workstreams by most-recently-updated first. */
export function sortByRecency(items: WorkstreamSummary[]): WorkstreamSummary[] {
  return items
    .slice()
    .sort((a, b) => (b.lastUpdated ?? '').localeCompare(a.lastUpdated ?? ''));
}

/** A named group of workstreams for the board (a stage column or an engineer). */
export interface WorkstreamGroup {
  /** Stable group key. */
  key: string;
  /** Display label. */
  label: string;
  /** Optional badge tone (used for stage groups). */
  tone?: BadgeTone;
  items: WorkstreamSummary[];
}

/**
 * Groups workstreams by owning engineer, alphabetical by alias, cards within a
 * group most-recently-updated first.
 */
export function groupByEngineer(
  items: WorkstreamSummary[],
): WorkstreamGroup[] {
  const byEngineer = new Map<string, WorkstreamSummary[]>();
  for (const w of items) {
    const key = w.engineer || '—';
    const list = byEngineer.get(key) ?? [];
    list.push(w);
    byEngineer.set(key, list);
  }
  return Array.from(byEngineer.keys())
    .sort((a, b) => a.localeCompare(b))
    .map((key) => ({
      key,
      label: key,
      items: sortByRecency(byEngineer.get(key) ?? []),
    }));
}

/**
 * Groups workstreams into pipeline-stage columns, ordered along the pipeline.
 * Only stages that actually have workstreams are emitted.
 */
export function groupByStage(items: WorkstreamSummary[]): WorkstreamGroup[] {
  const byStage = new Map<string, WorkstreamSummary[]>();
  for (const w of items) {
    const key = w.currentStage || '—';
    const list = byStage.get(key) ?? [];
    list.push(w);
    byStage.set(key, list);
  }
  return Array.from(byStage.keys())
    .sort((a, b) => stageRank(a) - stageRank(b))
    .map((key) => ({
      key,
      label: key,
      tone: stageTone(key),
      items: sortByRecency(byStage.get(key) ?? []),
    }));
}

/** True when the alias is already a follower (case-insensitive). */
export function isFollowing(followers: string[] | undefined, alias: string): boolean {
  const a = alias.trim().toLowerCase();
  if (!a) return false;
  return (followers ?? []).some((f) => f.trim().toLowerCase() === a);
}

/** Formats an ISO timestamp as a compact local date. */
export function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
